import type { NextSeoProps } from "next-seo";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { DefaultTitleTemplate } from "shared/components/meta/consts";
import { useDynamicMetaTags, useKeywordMetaTags } from "shared/components/meta/hooks";
import { useCanonicalUrl } from "shared/hooks";

import { PageSeo } from "scmp-app/components/meta";
import type { topicCollectionSeoCollection$key } from "scmp-app/queries/__generated__/topicCollectionSeoCollection.graphql";

type Props = {
  extraSeoOptions?: NextSeoProps;
  reference: topicCollectionSeoCollection$key;
};

export const TopicCollectionSeo: FunctionComponent<Props> = ({
  extraSeoOptions = {},
  reference,
}) => {
  const topicCollection = useFragment(
    graphql`
      fragment topicCollectionSeoCollection on Collection {
        name
        description {
          text
        }
        urlAlias
        ...hooksUseDynamicMetaTags
      }
    `,
    reference,
  );

  const metaTags = useDynamicMetaTags(topicCollection);
  const keywordTags = useKeywordMetaTags(metaTags.controlled.keywords ?? topicCollection.name);

  let titleTemplate = DefaultTitleTemplate;
  const title = useMemo(
    () => metaTags.controlled.title ?? topicCollection.name,
    [metaTags.controlled.title, topicCollection.name],
  );
  if (metaTags.controlled.title) {
    titleTemplate = "%s";
  }

  const description = topicCollection.description?.text ?? undefined;

  const { canonicalUrl } = useCanonicalUrl(topicCollection.urlAlias);

  return (
    <PageSeo
      {...extraSeoOptions}
      additionalMetaTags={[...keywordTags]}
      canonical={canonicalUrl}
      description={description}
      isAppLinksDisabled
      openGraph={{ description, title, url: canonicalUrl }}
      title={title}
      titleTemplate={titleTemplate}
    />
  );
};

TopicCollectionSeo.displayName = "TopicCollectionSeo";
